import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLogout } from '../../../Account/hooks/useLogout';
import CLOSE from '../../../assets/imgs/icon/close.svg';
import MENU from '../../../assets/imgs/icon/menu.svg';
import Footer from '../../../Footer';
import { Flex } from '../../../GlobalComponents/Flex';
import { IconButton } from '../../../GlobalComponents/IconButton';
import { useAppDispatch, useAppSelector } from '../../../reduxstore/hooks';
import { ACCOUNT_LINKS } from '../../../config';
import { setStoreSelection } from '../../../domains/UI/store/slice';

export const SideDrawer = (): JSX.Element => {
	const [showMobileNav, setShowMobileNav] = useState(false);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const logout = useLogout();
	const isAuthenticated = useAppSelector(
		state => state.authReducer.accessToken
	);

	const moveTo = (url: string) => {
		handleClose();
		navigate(url);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const storeSelection = useAppSelector(
		state => state.uiReducer.storeSelection
	);

	const handleClose = () => {
		setShowMobileNav(false);
	};

	const handleShow = () => {
		setShowMobileNav(!showMobileNav);
	};

	const handleLogout = () => {
		logout();
		navigate('/');
	};

	useEffect(() => {
		if (
			storeSelection.source === 'FIND_STORE' &&
			!storeSelection.isOpen &&
			!showMobileNav
		) {
			setShowMobileNav(!showMobileNav);
			dispatch(setStoreSelection({ isOpen: false }));
		}
	}, [storeSelection, showMobileNav, setShowMobileNav, dispatch]);

	return (
		<>
			<IconButton
				url={MENU}
				alt={'menu'}
				height={20}
				onClick={handleShow}
			></IconButton>

			<DrawerContainer $show={showMobileNav} id="container">
				<Backdrop onClick={handleClose} />
				<Drawer>
					<Flex
						$alignItems="flex-start"
						$flexDirection="row"
						$placeContent="flex-start flex-end"
						$padding="16px"
						$height="auto"
						$columnGap="10px"
					>
						<IconButton
							url={CLOSE}
							alt="X"
							onClick={handleClose}
						></IconButton>
					</Flex>
					<nav style={{ width: '100%' }}>
						<List onClick={() => setShowMobileNav(false)}>
							<ListItem>
								<Link to="/menu">Menu</Link>
							</ListItem>
							<ListItem
								onClick={() =>
									dispatch(
										setStoreSelection({
											isOpen: true,
											source: 'FIND_STORE'
										})
									)
								}
							>
								Find a Store
							</ListItem>
							<ListItem>
								<Link
									to="https://feedback.biggby.com/support/home"
									target="_blank"
									rel="noopener noreferrer"
								>
									Help
								</Link>
							</ListItem>

							<Divider />
							{!isAuthenticated && (
								<>
									<ListItem>
										<Link to={'/login'}>Sign In</Link>
									</ListItem>
									<ListItem>
										<a
											href={
												'https://accounts.biggby.com/register'
											}
										>
											Sign Up
										</a>
									</ListItem>
								</>
							)}
						</List>
						{isAuthenticated && (
							<List
								$authenticated
								onClick={() => setShowMobileNav(false)}
							>
								{ACCOUNT_LINKS.map(link => (
									<ListItem key={link.name} $authenticated>
										<Link
											style={{
												color: '#575757'
											}}
											to={link.link}
										>
											{link.name}
										</Link>
									</ListItem>
								))}

								<Divider
									style={{
										borderBottom: '1px solid #575757'
									}}
								/>
								<ListItem
									$authenticated
									$logout
									className="navlist logout"
									onClick={handleLogout}
									color="#575757"
								>
									Sign Out
								</ListItem>
							</List>
						)}
					</nav>
					<Footer moveTo={moveTo} />
				</Drawer>
			</DrawerContainer>
		</>
	);
};

const DrawerContainer = styled.div<SideDrawerContainerProps>`
	width: 100vw;
	height: 100%;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 10;
	display: ${({ $show }) => ($show ? 'block' : 'none')};
`;

const Drawer = styled(Flex)`
	flex-direction: column;
	place-content: flex-start space-between;
	align-items: flex-start;
	position: absolute;
	width: 80vw;
	height: 100%;
	right: 0;
	top: 0;
	background-color: #fff;
`;

const Backdrop = styled.div`
	position: absolute;
	width: 100vw;
	height: 100%;
	right: 0;
	top: 0;
	background-color: rgba(45, 45, 45, 0.5);
`;

const List = styled.ul<ListProps>`
	display: flex;
	flex-direction: column;
	list-style: none;
	/* margin-top: ${({ $authenticated }) => ($authenticated ? 0 : '10%')};
	padding-top: ${({ $authenticated }) => ($authenticated ? 0 : '48px')}; */
	margin-bottom: 0;
	padding-left: 48px;
	padding-right: 48px;
`;

const ListItem = styled.li<ListProps>`
	font-size: ${({ $authenticated, $logout }) =>
		$authenticated || $logout ? '20px' : ' 24px'};
	line-height: 1.7;
	font-weight: 600;
	list-style: none;
	color: ${({ $color }) => ($color ? `${$color}` : '#f47920')};
	@media screen and (max-width: 375px) {
		font-size: ${({ $authenticated }) =>
			$authenticated ? '18px' : ' 24px'};
	}
`;

const Divider = styled.div`
	width: 100%;
	border-bottom: 1px solid #f47920;
	height: 1px;
	margin: 10px 0;
`;

interface SideDrawerContainerProps {
	$show: boolean;
}

interface ListProps {
	$authenticated?: boolean;
	$logout?: boolean;
	$color?: string;
}
